import { useTranslation } from 'next-i18next';
import { FunctionComponent } from 'react';
import type { Market } from '@bridebook/toolbox/src/gazetteer';
import { IUISupplier } from '@bridebook/toolbox/src/types';
import { Box, Button, H5, MultilineEllipsis, Rating } from '@bridebook/ui';
import { Properties } from '@bridebook/web/app-shared/components/supplier-properties/supplier-properties';
import { ISharedTypes as ISupplierCardProps } from '@bridebook/web/app-shared/components/supplier/supplier-card/supplier-card';
import {
  supplierHasReviews,
  supplierHasVideoTours,
} from '@bridebook/web/lib/search/utils/supplier-tiles';
import SupplierDetailsTags from './supplier-details-tags';
import componentStyles from './supplier-info-card-content.style';

interface IProps
  extends Pick<
    ISupplierCardProps,
    'enquiryCTAText' | 'fullBleed' | 'multiLineStyle' | 'onContactSupplierClick'
  > {
  contact?: boolean;
  market: Market;
  minified?: boolean;
  showDetailsTags?: boolean;
  supplier: IUISupplier;
}

const SupplierInfoCardContent: FunctionComponent<IProps> = ({
  contact,
  enquiryCTAText,
  fullBleed,
  market,
  minified,
  multiLineStyle,
  onContactSupplierClick,
  showDetailsTags,
  supplier,
}: IProps) => {
  const { t } = useTranslation('supplierCard');
  const { name, reviewsCount, reviewsStars, hasQualityPricing } = supplier;
  const styles = componentStyles({ minified, fullBleed });
  const showReviews = supplierHasReviews(supplier);
  const hasVideoTours = supplierHasVideoTours(supplier);
  const hasInfoNote = hasVideoTours || hasQualityPricing;
  const infoNoteText = hasVideoTours ? t('infoCard.videoTours') : t('infoCard.pricing');

  return (
    <>
      <Box style={fullBleed ? styles.fullBleedWrapper : styles.wrapper}>
        <Box style={styles.infoWrap}>
          <Box style={styles.name}>
            <MultilineEllipsis
              lines={minified ? 1 : 2}
              fontSize={minified ? 14 : 16}
              lineHeight={18}
              style={multiLineStyle}>
              {fullBleed ? <H5 style={styles.supplierName}>{name}</H5> : name}
            </MultilineEllipsis>
          </Box>
          {!minified && <Properties market={market} supplier={supplier} />}
          <Box style={styles.secondaryInfoWrap}>
            {!!showReviews && (
              <Box style={styles.ratingWrap}>
                <Rating rating={Number(reviewsStars)} width={10} />
                <Box style={styles.reviewAmount}>({reviewsCount})</Box>
              </Box>
            )}
            {!minified && hasInfoNote && <Box style={styles.availableInfoNote}>{infoNoteText}</Box>}
          </Box>
        </Box>
        {showDetailsTags && <SupplierDetailsTags supplier={supplier} />}{' '}
        {contact && (
          <Button
            theme="primary"
            width="block"
            text={enquiryCTAText}
            onClick={(e) => {
              // @ts-ignore
              onContactSupplierClick(e, supplier);
            }}
            name="toggle-enquiry"
          />
        )}
      </Box>
    </>
  );
};

export default SupplierInfoCardContent;
